<template>
  <v-menu v-tooltip:bottom="$t('navigation.labels.selectLanguage')">
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        data-testid="locale-selector"
        variant="text"
        :color="componentProps.color"
        :aria-label="`${$t('navigation.labels.siteLanguage')}: ${currentLocaleLabel}`">
        <v-icon size="18" class="mr-1">mdi-translate</v-icon>
        <span
          :class="[
            'typography-caption',
            componentProps.hideLabelOnMobile ? 'd-none d-sm-inline' : 'd-inline'
          ]"
          aria-hidden="true">
          {{ $t('navigation.labels.siteLanguage') }}: {{ currentLocaleLabel }}
        </span>
      </v-btn>
    </template>

    <v-list density="compact">
      <v-list-item
        v-for="localeOption in locales"
        :key="localeOption.code"
        :active="localeOption.code === locale"
        @click="selectLocale(localeOption.code)">
        <template #prepend>
          <span class="flag-emoji mr-2">{{ localeOption.flag }}</span>
        </template>
        <v-list-item-title>{{ localeOption.label }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@/composables';
import {
  useLocaleHelpers,
  localeToReadableLanguageMap
} from '@satellite/composables/useLocaleHelpers.ts';
import { usePreferredLanguage } from '@satellite/composables/usePreferredLanguage.ts';
import { useLocaleAnalytics } from '@/composables/useLocaleAnalytics';

const componentProps = defineProps<{ color?: string; hideLabelOnMobile?: boolean }>();

const { locale } = useI18n();
const { meRef } = useAuth();
const { setLocale: setUserLocale } = useLocaleHelpers(() => meRef.value?.id);
const { setLocale: setGuestLocale } = useLocaleHelpers();
const { persistPreferredLanguageOnChange } = usePreferredLanguage();
const { trackApplicationLanguageSwitched } = useLocaleAnalytics();

const locales = computed(() =>
  Object.entries(localeToReadableLanguageMap).map(([code, { label, flag }]) => ({
    code,
    label,
    flag
  }))
);

const currentLocaleLabel = computed(
  () =>
    localeToReadableLanguageMap[locale.value as keyof typeof localeToReadableLanguageMap]?.label ??
    locale.value
);

async function selectLocale(code: string) {
  if (code === locale.value) {
    return;
  }

  locale.value = code;

  trackApplicationLanguageSwitched({
    application_language:
      localeToReadableLanguageMap[code as keyof typeof localeToReadableLanguageMap]?.label ?? code
  });

  const userId = meRef.value?.id;
  if (userId) {
    setUserLocale(code);
    await persistPreferredLanguageOnChange(userId, code);
  }

  setGuestLocale(code);
}
</script>

<style lang="scss" scoped>
.flag-emoji {
  font-size: 20px;
  line-height: 1;
}
</style>
